import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatEUR, formatDateTime, buildTrackingURL } from '@/lib/api';
import { Btn, Card, SectionTitle, Chip } from '@/components/ui-ios';
import { FilePlus2, FilePen, Search, CalendarDays, CalendarRange, Tags, Wallet, FileStack, TrendingUp, Coins, Receipt, ArrowUpRight, Inbox, AlertTriangle } from 'lucide-react';
import CourierBadge from '@/components/CourierBadge';
import EsitoBadge from '@/components/EsitoBadge';
import SystemStatsCard from '@/components/SystemStatsCard';
import AppStatsCard from '@/components/AppStatsCard';
import { useCatalog } from '@/context/CatalogContext';
import { useAuth } from '@/context/AuthContext';

function KPI({ tone = '', label, value, sub, icon: Icon, testid }) {
  return (
    <div className={`ios-kpi ${tone}`} data-testid={`dashboard-kpi-card-${testid}`}>
      <div className="flex items-center justify-between">
        <span className="ios-kpi-label">{Icon && <Icon className="h-4 w-4" />} {label}</span>
      </div>
      <div className="ios-kpi-value" data-testid={`dashboard-kpi-value-${testid}`}>{value}</div>
      {sub && <div className="text-xs text-[color:var(--text-secondary)]">{sub}</div>}
    </div>
  );
}

const TILES = [
  { to: '/raccomandate/nuova', title: 'Crea nuova raccomandata', desc: 'Nuova spedizione + ricevuta', icon: FilePlus2, key: 'nuova' },
  { to: '/raccomandate/cerca?action=modifica', title: 'Modifica Dati Raccomandata', desc: 'Seleziona e modifica', icon: FilePen, key: 'modifica' },
  { to: '/raccomandate/cerca', title: 'Cerca Raccomandata', desc: 'Per mittente, destinatario, codice', icon: Search, key: 'cerca' },
  { to: '/raccomandate/ritiro', title: 'Ritiro Raccomandata', desc: 'Ritira allo sportello (giacenze)', icon: Inbox, key: 'ritiro' },
  { to: '/totali/giornaliero', title: 'Totale Giornaliero', desc: 'Incasso e profitto di oggi', icon: CalendarDays, key: 'giornaliero' },
  { to: '/totali/mensile', title: 'Totale Mensile', desc: 'Riepilogo del mese', icon: CalendarRange, key: 'mensile' },
  { to: '/prezzi', title: 'Modifica Prezzi Vendita', desc: 'Listino per tipo/zona/peso', icon: Tags, key: 'prezzi' },
  { to: '/costi-agenzia', title: 'Costo Raccomandate AGENZIA', desc: 'Costi pagati dall\'agenzia', icon: Wallet, key: 'costi', adminOnly: true },
  { to: '/moduli', title: 'Moduli consegna Bolgetta', desc: 'Accettazione, Distinta Analitica', icon: FileStack, key: 'moduli' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { labelOf } = useCatalog();
  // Re-use the user already loaded by AuthContext instead of refetching
  // /auth/me here — saves a round-trip on every dashboard load (and on
  // 100+ concurrent users that's a meaningful win).
  const { user } = useAuth();
  const [daily, setDaily] = useState(null);
  const [latest, setLatest] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [giacenza, setGiacenza] = useState({ total: 0, overdue: 0 });
  // Raccomandate PostaCentrale stuck without a final outcome > 4 days from
  // creation. Loaded best-effort like giacenza — backend already filters by
  // courier and esito.
  const [inRitardo, setInRitardo] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, l, c, g, r] = await Promise.all([
          api.get('/totals/daily'), api.get('/raccomandate?limit=8'),
          api.get('/couriers'),
          api.get('/raccomandate/giacenza').catch(() => ({ data: [] })),
          api.get('/raccomandate/in-ritardo').catch(() => ({ data: [] })),
        ]);
        setDaily(d.data); setLatest(l.data); setCouriers(c.data);
        const items = Array.isArray(g.data) ? g.data : [];
        setGiacenza({ total: items.length, overdue: items.filter(r2 => (r2.giacenza_days || 0) >= 30).length });
        setInRitardo(Array.isArray(r.data) ? r.data : []);
      } catch (e) { /* dashboard widgets best-effort — auth/quick checks will redirect if needed */ }
    })();
  }, []);
  const courierMap = Object.fromEntries((couriers || []).map(c => [c.id, c]));
  const isAdmin = user?.role === 'superadmin';
  // Tiles list is static; the only thing that may change is `isAdmin`. Memoize
  // so the filter doesn't re-run on every render (and so React.memo on Link
  // would still work if used).
  const visibleTiles = useMemo(
    () => TILES.filter(t => !t.adminOnly || isAdmin),
    [isAdmin],
  );
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buongiorno';
    if (h < 18) return 'Buon pomeriggio';
    return 'Buonasera';
  })();

  return (
    <div className="space-y-6">
      {/* Hero greeting */}
      <div className="ios-card p-6 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-gradient-to-br from-orange-200/60 to-amber-200/0 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-[color:var(--text-secondary)] mb-1">{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
            <h2 className="text-3xl font-semibold tracking-tight">{greeting}, {user?.nome?.split(' ')[0] || 'Operatore'}</h2>
            <p className="text-sm text-[color:var(--text-secondary)] mt-1">Ecco il riepilogo di oggi e le tue azioni rapide.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Btn variant="outline" onClick={() => navigate('/raccomandate/cerca')}><Search className="h-4 w-4" /> Cerca</Btn>
            <Btn
              variant={giacenza.total > 0 ? 'outline' : 'outline'}
              onClick={() => navigate('/raccomandate/ritiro')}
              testid="dashboard-ritiro-cta"
              className={giacenza.overdue > 0 ? 'border-rose-300 text-rose-700 hover:bg-rose-50' : ''}
            >
              <Inbox className="h-4 w-4" /> Ritiro raccomandata{giacenza.total > 0 ? ` · ${giacenza.total}` : ''}
            </Btn>
            <Btn variant="accent" onClick={() => navigate('/raccomandate/nuova')} testid="dashboard-new-cta"><FilePlus2 className="h-4 w-4" /> Nuova raccomandata</Btn>
          </div>
        </div>
      </div>

      {/* Giacenza overdue alert */}
      {giacenza.overdue > 0 && (
        <div
          role="button"
          onClick={() => navigate('/raccomandate/ritiro')}
          className="ios-card p-4 sm:p-5 bg-rose-50 border-rose-200 flex items-start gap-3 cursor-pointer hover:bg-rose-100 transition-colors"
          data-testid="dashboard-giacenza-alert"
        >
          <div className="h-10 w-10 rounded-xl bg-rose-100 inline-flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-rose-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-rose-900">
              {giacenza.overdue === 1
                ? '1 raccomandata ha superato i 30 giorni di giacenza'
                : `${giacenza.overdue} raccomandate hanno superato i 30 giorni di giacenza`}
            </div>
            <p className="text-sm text-rose-900/80">
              Tempo massimo di giacenza maturato: provvedi al rinvio al mittente. Tocca per gestire.
            </p>
          </div>
          <ArrowUpRight className="h-5 w-5 text-rose-700 shrink-0" />
        </div>
      )}

      {/* PostaCentrale "in ritardo" alert — raccomandate ferme da oltre 4
          giorni dalla creazione senza un esito finale. Linkano direttamente
          alla pagina Cerca filtrata sui codici elencati così l'agenzia può
          contattare i portalettere e sollecitare un esito. */}
      {inRitardo.length > 0 && (
        <div
          role="button"
          onClick={() => navigate('/raccomandate/cerca?filter=in-ritardo')}
          className="ios-card p-4 sm:p-5 bg-amber-50 border-amber-200 flex items-start gap-3 cursor-pointer hover:bg-amber-100 transition-colors"
          data-testid="dashboard-ritardo-alert"
        >
          <div className="h-10 w-10 rounded-xl bg-amber-100 inline-flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-amber-900" data-testid="dashboard-ritardo-count">
              {inRitardo.length === 1
                ? '1 raccomandata PostaCentrale ferma da oltre 4 giorni'
                : `${inRitardo.length} raccomandate PostaCentrale ferme da oltre 4 giorni`}
            </div>
            <p className="text-sm text-amber-900/80">
              Nessun esito registrato dalla presa in carico. Tocca per visualizzarle.
            </p>
            {/* Show the 3 oldest tracking codes inline so the operator can
                immediately recognize them without leaving the dashboard. */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {inRitardo.slice(0, 3).map(r => (
                <span key={r.id} className="font-mono text-[11px] px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900">
                  {r.tracking_code} · {r.ritardo_days}g
                </span>
              ))}
              {inRitardo.length > 3 && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900">
                  +{inRitardo.length - 3} altre…
                </span>
              )}
            </div>
          </div>
          <ArrowUpRight className="h-5 w-5 text-amber-700 shrink-0" />
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="incasso" tone="" icon={TrendingUp} label="Incasso oggi" value={daily ? formatEUR(daily.revenue) : '—'} sub={daily ? `${daily.count} raccomandate` : ''} />
        <KPI testid="costo" tone="k-blue" icon={Wallet} label="Spesa agenzia" value={daily ? formatEUR(daily.cost) : '—'} />
        <KPI testid="profitto" tone="k-green" icon={Coins} label="Profitto oggi" value={daily ? formatEUR(daily.profit) : '—'} sub="Incasso - spesa" />
        <KPI testid="numero" tone="k-violet" icon={Receipt} label="N° spedizioni" value={daily ? daily.count : 0} />
      </div>

      {/* SuperAdmin: dati business in cima (grande), stato server piccolo/collassabile in fondo */}
      {isAdmin && <AppStatsCard />}
      {isAdmin && (
        <details className="group" data-testid="sa-server-stats-details">
          <summary className="cursor-pointer text-sm font-medium text-[color:var(--text-secondary)] hover:text-zinc-900 select-none flex items-center gap-2 py-2">
            <span className="inline-block w-4 h-4 transition-transform group-open:rotate-90">▶</span>
            Stato server (CPU / RAM / Disco)
          </summary>
          <div className="mt-2">
            <SystemStatsCard />
          </div>
        </details>
      )}

      {/* Tiles */}
      <div>
        <SectionTitle title="Menu rapido" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {visibleTiles.map(t => {
            const Icon = t.icon;
            return (
              <Link key={t.key} to={t.to} className="ios-tile" data-testid={`dashboard-tile-${t.key}`}>
                <div className="ios-tile-icon"><Icon className="h-5 w-5" /></div>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Latest raccomandate */}
      <Card className="p-2 sm:p-4">
        <div className="px-2 sm:px-2 pt-2 pb-3 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight">Ultime raccomandate</h2>
          <Btn variant="ghost" size="sm" onClick={() => navigate('/raccomandate/cerca')}>Vedi tutte <ArrowUpRight className="h-4 w-4" /></Btn>
        </div>
        <div className="overflow-x-auto">
          <table className="ios-table">
            <thead>
              <tr>
                <th>Data</th><th>Codice</th><th>Destinatario</th><th>Tipo</th><th>Corriere</th><th className="num">Prezzo</th><th>Esito</th>
              </tr>
            </thead>
            <tbody>
              {latest.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[color:var(--text-secondary)]">Nessuna raccomandata. Inizia creandone una nuova.</td></tr>}
              {latest.map(r => {
                const c = courierMap[r.courier_id];
                const url = c ? buildTrackingURL(c.tracking_url_template, r.tracking_code) : '';
                return (
                  <tr key={r.id}>
                    <td className="text-xs text-[color:var(--text-secondary)] whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                    <td>{url ? <a href={url} target="_blank" rel="noreferrer" data-testid={`raccomandata-tracking-link-${r.id}`} className="font-mono text-[#0A66D6] hover:underline">{r.tracking_code || '—'}</a> : <span className="font-mono">{r.tracking_code || '—'}</span>}</td>
                    <td className="font-medium">{r.destinatario?.nome || '—'}</td>
                    <td><Chip tone="neutral">{labelOf(r.tipo)}</Chip></td>
                    <td><CourierBadge name={r.courier_name} /></td>
                    <td className="num font-semibold">{formatEUR(r.price_paid)}</td>
                    <td><EsitoBadge value={r.esito} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
